import { PermissionId, isPermissionVisibleInUi, workspacesConfig } from 'utils/powerLevelsConfig';

import {
  PermissionCategory,
  PERMISSION_CATEGORY_IDS,
  PermissionCategoryId,
  WorkspacePowerLevels,
  WorkspaceType,
} from './types';

export const workspaceKinds = [WorkspaceType.WORKSPACE, WorkspaceType.SUB_WORKSPACE];

export const WORKSPACE_META_VERSION = 1; //Versioning through version field in custom event

const CATEGORY_LABEL_KEYS: Record<PermissionCategoryId, string> = {
  workspace: 'workspace_roles.permission_categories.workspace',
  chat: 'workspace_roles.permission_categories.chat',
  meeting: 'workspace_roles.permission_categories.meeting',
};

interface GetPermissionCategoriesArgs {
  actorPowerLevels: WorkspacePowerLevels;
  /** Role editor: workspace permissions must be strictly below the actor's workspace PL. */
  exclusiveWorkspaceCap?: boolean;
}

const isPermissionWithinActorCap = (
  entry: (typeof workspacesConfig)[PermissionId],
  scope: PermissionCategoryId,
  actorPowerLevels: WorkspacePowerLevels,
  exclusiveWorkspaceCap: boolean
): boolean => {
  const actorPl = actorPowerLevels[scope];
  if (scope === 'workspace' && exclusiveWorkspaceCap) return entry.powerLevel < actorPl;
  return entry.powerLevel <= actorPl;
};

/**
 * Actor PLs for the role create/edit UI. Chat and meeting scopes are fully grantable
 * (MIP-035: manage-roles does not require the actor to hold chat/meeting authority).
 * Pair with `exclusiveWorkspaceCap` on `getPermissionCategories` for workspace scope.
 */
export const getRoleEditorActorPowerLevels = (actorPowerLevels: WorkspacePowerLevels): WorkspacePowerLevels => ({
  workspace: actorPowerLevels.workspace,
  chat: Infinity,
  meeting: Infinity,
});

/** Strips workspace permissions at or above the actor PL before persisting a role. */
export const clampRoleEditorWorkspacePermissions = (
  permissions: Record<PermissionId, boolean>,
  actorWorkspacePl: number
): Record<PermissionId, boolean> => {
  const clamped = { ...permissions };

  for (const entry of Object.values(workspacesConfig)) {
    if (entry.scope === 'workspace' && entry.powerLevel >= actorWorkspacePl) {
      clamped[entry.id] = false;
    }
  }

  return clamped;
};

/**
 * V2 permission categories — returns one `PermissionCategory` per scope, in the
 * fixed order Workspace → Chat → Meeting (see `PERMISSION_CATEGORY_IDS`). Each
 * category contains the permissions in `workspacesConfig` whose `scope` matches
 * the category and whose PL is within the actor cap for that scope.
 *
 * See spec/tech.md §3.3. Role editors pass `getRoleEditorActorPowerLevels(...)` with
 * `exclusiveWorkspaceCap: true` so workspace grants are strictly below actor PL.
 */
export const getPermissionCategories = ({
  actorPowerLevels,
  exclusiveWorkspaceCap = false,
}: GetPermissionCategoriesArgs): PermissionCategory[] =>
  PERMISSION_CATEGORY_IDS.map((scope) => ({
    id: scope,
    labelKey: CATEGORY_LABEL_KEYS[scope],
    permissions: Object.values(workspacesConfig).filter(
      (entry) =>
        isPermissionVisibleInUi(entry) &&
        entry.scope === scope &&
        isPermissionWithinActorCap(entry, scope, actorPowerLevels, exclusiveWorkspaceCap)
    ),
  }));

/** Splits a permission list into two columns filled top-to-bottom (first half left, second half right). */
export const splitPermissionsIntoTwoColumns = <T>(items: readonly T[]): [T[], T[]] => {
  const splitIndex = Math.ceil(items.length / 2);
  return [items.slice(0, splitIndex), items.slice(splitIndex)];
};
